import { WatchInterface, WatchMessage } from "./watchInterface";
import { Observer } from "rxjs";
import shortUUID from "short-uuid";
import * as Sentry from "@sentry/react";
import { wakeUpMessage, WatchWakeUpRequest } from "./messages/wakeUp";
import { MessageTypes } from "./messageTypesEnum";
import { getMatchesListMessage } from "./messages/getMatchesList";
import { getConfiguration } from "./messages/getConfiguration";
import { ChunkMessage, MessageChunkType } from "./TrackingChunkAssembler";
import { handleChunk } from "./messages/gpsHrReceived";
import {
    handleResultReceived,
    ResultReceived,
} from "./messages/resultRecieved";
import { tokenMessage } from "./messages/getToken";
import { DeviceInfo, deviceInfoResponse } from "./messages/deviceInfo";
import { store } from "../../redux/store";
import { prepareMatchSummaryForWatch } from "../matchService";
import { getMatchesSummaryMessage } from "./messages/getMatchesSummary";
import { getMatch } from "./messages/getMatch";

export enum WatchType {
    native = "native", // e.g. Apple Watch on iPhone or WearOS on Android
    garmin = "garmin",
}

export const FREE_USER_EXPIRES_IN_MS = 15778800000; // 6 months in millis

export class WatchService {
    nativeWatch: WatchInterface;
    garminWatch: WatchInterface;

    /**
     * @param nativeWatch The Apple Watch or WearOS watch
     * @param garminWatch The Garmin watch. Should always be set and will detect any watch present
     */
    constructor(nativeWatch: WatchInterface, garminWatch: WatchInterface) {
        this.nativeWatch = nativeWatch;
        this.garminWatch = garminWatch;

        const self = this;

        let nativeWatchObserver: Observer<any> = {
            next: (payload: any) => {
                console.log(`[WatchService] Received ${payload.messageId}`);
                self.onMessageReceived(payload, nativeWatch).catch((error) => {
                    console.log(
                        `[WatchService] Native Error handling message ${
                            payload.messageId
                        } ${JSON.stringify(error)}`
                    );
                    Sentry.captureException(error);
                });
            },
            error: (error) =>
                console.log(`[WatchService] Native The error was ${error}`), // TODO not sure what to do
            complete: () =>
                console.log(
                    "[WatchService] Native The subscription has completed"
                ), // TODO not sure what to do
        };

        let garminWatchObserver: Observer<any> = {
            next: (payload: any) => {
                console.log(`[WatchService] Received ${payload.messageId}`);
                self.onMessageReceived(payload, garminWatch).catch((error) => {
                    console.log(
                        `[WatchService] Garmin Error handling message ${
                            payload.messageId
                        } ${JSON.stringify(error)}`
                    );
                    Sentry.captureException(error);
                });
            },
            error: (error) =>
                console.log(`[WatchService] Garmin The error was ${error}`), // TODO not sure what to do
            complete: () =>
                console.log(
                    "[WatchService] Garmin The subscription has completed"
                ), // TODO not sure what to do
        };

        this.nativeWatch.messageReceivedStream().subscribe(nativeWatchObserver);
        this.garminWatch.messageReceivedStream().subscribe(garminWatchObserver);
    }

    /**
     * This function is called when a message is received from the Watch.
     * It will create and send any required response to the watch.
     * @param request
     * @param watchInterface
     */
    onMessageReceived = async (
        request: WatchMessage,
        watchInterface: WatchInterface
    ) => {
        const username = store.getState().auth?.session?.user_id;
        const databaseLoading = store.getState().matches.loading;

        if (!username || databaseLoading !== false) {
            console.log("The app isn't ready yet!");
            return;
        }

        Sentry.configureScope((scope: Sentry.Scope) => {
            scope.addAttachment({
                filename: "watch-message.json",
                data: JSON.stringify(request),
            });
        });

        let messageId: MessageTypes | undefined = request.messageId;

        if (!messageId) {
            // TODO remove this once we're sure all watches are on latest version >6.0.0
            // old watch app used payloadType instead of messageId. Allow backwards compatability
            if (request.payloadType) {
                messageId = request.payloadType;
                request = {
                    ...request,
                    messageId: messageId,
                    payloadType: undefined,
                };
            } else {
                Sentry.captureMessage(
                    "[WatchService.onMessageReceived] Unknown BT payload missing messageId"
                );
                Sentry.configureScope((scope) => {
                    scope.clearAttachments();
                });
                return;
            }
        }

        let responseMsgPromise: Promise<any> | undefined;

        console.log(`Message received from watch: ${messageId}`, request);

        try {
            switch (messageId) {
                case MessageTypes.WAKE_UP:
                    const wakeUpRequest = request as WatchWakeUpRequest;
                    responseMsgPromise = wakeUpMessage(wakeUpRequest);
                    break;
                case MessageTypes.GET_MATCHES_LIST:
                    // TODO sending tokens on other request types?
                    responseMsgPromise = getMatchesListMessage(
                        request,
                        undefined
                    );
                    break;
                case MessageTypes.GET_MATCHES_SUMMARY_REQUEST:
                    responseMsgPromise = getMatchesSummaryMessage(
                        request,
                        undefined
                    );
                    break;

                case MessageTypes.GET_MATCH:
                    responseMsgPromise = getMatch(request, undefined);
                    break;
                case MessageTypes.GET_CONFIGURATION:
                    responseMsgPromise = getConfiguration(request, undefined);
                    break;
                case MessageTypes.SEND_RESULT:
                    const resultRequest = request as ResultReceived;
                    responseMsgPromise = handleResultReceived(
                        resultRequest,
                        undefined
                    );
                    break;
                case MessageTypes.TOKEN_REQUEST:
                    responseMsgPromise = tokenMessage(request);
                    break;
                case MessageTypes.GPS_RECEIVED:
                    const gpsRequest = request as unknown as ChunkMessage;
                    await handleChunk(MessageChunkType.gps, gpsRequest);
                    break;
                case MessageTypes.HR_RECEIVED:
                    const hrRequest = request as unknown as ChunkMessage;
                    await handleChunk(MessageChunkType.hr, hrRequest);
                    break;
                case MessageTypes.DEVICE_INFO_RESPONSE:
                    // This is only implemented on wearOS
                    const deviceInfo = request as unknown as DeviceInfo;
                    await deviceInfoResponse(deviceInfo);
                    break;
                default:
                    // If not, we don't recognise this message
                    Sentry.captureMessage(
                        `[WatchService.onMessageReceived] Unknown BT payload`
                    );
                    console.log("Unknown payload");
            }
        } catch (error: any) {
            const message = `[WatchService.onMessageReceived()] something went wrong ${JSON.stringify(
                error
            )}`;
            console.log(message);
            Sentry.captureMessage(message);
        }

        // Send the response if there is any
        if (responseMsgPromise) {
            let responseMessage: any;
            try {
                responseMessage = await responseMsgPromise;
                if (!responseMessage) {
                    return;
                }
                console.log(
                    "Sending response to watch: ",
                    JSON.stringify(responseMessage)
                );
                // Only send reply on same interface that received it
                await watchInterface.sendMessage(responseMessage);
                Sentry.addBreadcrumb({
                    message: `[WatchService.onMessageReceived()] sent ${responseMessage.messageId} response to ${watchInterface.name}`,
                });
            } catch (error: any) {
                const message = `[WatchService.onMessageReceived()] unable to send response to ${watchInterface.name}. ${error.message}`;
                Sentry.addBreadcrumb({ message });
                console.error(message, error);
            }
        }

        // Clear attachments
        Sentry.configureScope((scope) => {
            scope.clearAttachments();
        });
    };

    /**
     * Send a message from the phone to the watch.
     * @param payload
     * @param watchType
     */
    sendMessage = (payload: any, watchType: WatchType) => {
        if (watchType === WatchType.garmin) {
            return this.garminWatch.sendMessage(payload);
        }
        return this.nativeWatch.sendMessage(payload);
    };

    sendTestMessage = (watchType: WatchType = WatchType.native) => {
        return this.sendMessage(
            {
                messageId: "pullMatchId",
                id: shortUUID().uuid(),
            },
            watchType
        );
    };
}
